import { useSession } from '@zhiyue/core';
import { useAccess } from '@umijs/max';
import { Spin } from 'antd';
import React from 'react';
import { exitApi } from '@/services/bff/exit.api';
import { portfolioApi } from '@/services/bff/portfolio.api';
import { hasOAuth2Session } from '@/services/oauth2';
import { PERMISSION_VIEW_CODES } from '@/permissions/definitions';
import { createEmptyExitState } from './emptyState';
import {
  mapApiExitProject,
  mapApiExitTransaction,
  mapApiTransactionToBuyer,
  type ExitProjectApi,
} from './apiMappers';
import {
  buildFundsFromPortfolio,
  mapPortfolioCompanyToExit,
} from './portfolioBridge';
import { exitManagementReducer } from './reducer';
import type {
  ExitManagementAction,
  ExitManagementState,
  ExitOpportunity,
  ExitProject,
  ExitTransaction,
} from './types';

const StateContext = React.createContext<ExitManagementState | null>(null);
const DispatchContext =
  React.createContext<
    ((action: ExitManagementAction) => void | Promise<void>) | null
  >(null);
const ApiContext = React.createContext<{
  apiReady: boolean;
  refreshFromApi: () => Promise<void>;
} | null>(null);

const toRecord = <T extends { id: string }>(items: T[]) =>
  Object.fromEntries(items.map((item) => [item.id, item]));

const loadExitFromApi = async (
  access: Record<string, boolean>,
): Promise<ExitManagementState> => {
  const base = createEmptyExitState();
  const canViewOpportunities = Boolean(access[PERMISSION_VIEW_CODES.opportunity]);
  const canViewProjects = Boolean(access[PERMISSION_VIEW_CODES.project]);
  const canViewTransactions = Boolean(access[PERMISSION_VIEW_CODES.transaction]);
  const [
    opportunities,
    projects,
    transactions,
    portfolioCompanies,
    buyers,
    conditions,
    paymentPlans,
    paymentRecords,
  ] = await Promise.all([
    canViewOpportunities ? exitApi.listOpportunities() : Promise.resolve([]),
    canViewProjects ? exitApi.listProjects() : Promise.resolve([]),
    canViewTransactions ? exitApi.listTransactions() : Promise.resolve([]),
    portfolioApi.listCompanies({ skipErrorHandler: true }).catch(() => []),
    canViewProjects ? exitApi.listBuyers() : Promise.resolve([]),
    canViewTransactions ? exitApi.listConditions() : Promise.resolve([]),
    canViewTransactions ? exitApi.listPaymentPlans() : Promise.resolve([]),
    canViewTransactions ? exitApi.listPaymentRecords() : Promise.resolve([]),
  ]);

  const mappedProjects = (projects as ExitProjectApi[]).map(mapApiExitProject);
  const mappedTransactions = transactions.map(mapApiExitTransaction);

  const buyersFromTransactions = Object.fromEntries(
    mappedTransactions
      .map((transaction) => mapApiTransactionToBuyer(transaction))
      .filter((buyer): buyer is NonNullable<typeof buyer> => Boolean(buyer))
      .map((buyer) => [buyer.id, buyer]),
  );

  const companies = toRecord(
    portfolioCompanies.map(mapPortfolioCompanyToExit),
  );
  const funds = buildFundsFromPortfolio(
    portfolioCompanies,
    opportunities,
    mappedProjects,
  );

  const mappedOpportunities = (opportunities as Array<ExitOpportunity & { fundUuid?: string }>).map(
    (item) => ({
      ...item,
      fundId: item.fundUuid || item.fundId,
      fundUuid: item.fundUuid,
    }),
  );

  return {
    ...base,
    companies,
    funds,
    buyers: { ...buyersFromTransactions, ...toRecord(buyers) },
    conditions: toRecord(conditions),
    paymentPlans: toRecord(paymentPlans),
    paymentRecords: toRecord(paymentRecords),
    opportunities: toRecord(mappedOpportunities) as Record<string, ExitOpportunity>,
    projects: toRecord(mappedProjects) as Record<string, ExitProject>,
    transactions: toRecord(mappedTransactions) as Record<
      string,
      ExitTransaction
    >,
  };
};

export const ExitManagementProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const access = useAccess() as Record<string, boolean>;
  const canViewOpportunities = Boolean(access[PERMISSION_VIEW_CODES.opportunity]);
  const canViewProjects = Boolean(access[PERMISSION_VIEW_CODES.project]);
  const canViewTransactions = Boolean(access[PERMISSION_VIEW_CODES.transaction]);
  const accessSignature = `${canViewOpportunities}:${canViewProjects}:${canViewTransactions}`;
  const [state, baseDispatch] = React.useReducer(
    exitManagementReducer,
    undefined,
    createEmptyExitState,
  );
  const [apiReady, setApiReady] = React.useState(false);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  const refreshFromApi = React.useCallback(async () => {
    const next = await loadExitFromApi({
      [PERMISSION_VIEW_CODES.opportunity]: canViewOpportunities,
      [PERMISSION_VIEW_CODES.project]: canViewProjects,
      [PERMISSION_VIEW_CODES.transaction]: canViewTransactions,
    });
    baseDispatch({ type: 'IMPORT_STATE', state: next });
    setApiReady(true);
  }, [accessSignature]);

  React.useEffect(() => {
    setApiReady(false);
  }, [accessSignature]);

  React.useEffect(() => {
    if (sessionLoading || !hasOAuth2Session() || !currentUser) {
      return;
    }
    void refreshFromApi();
  }, [refreshFromApi, sessionLoading, currentUser]);

  const dispatch = React.useCallback(
    (action: ExitManagementAction): void | Promise<void> => {
      switch (action.type) {
        case 'CONVERT_OPPORTUNITY_TO_PROJECT':
          return (async () => {
            await exitApi.convertOpportunity(action.opportunityId);
            await refreshFromApi();
          })();
        case 'UPDATE_OPPORTUNITY_STATUS':
          return (async () => {
            await exitApi.updateOpportunityStatus(action.opportunityId, action.status);
            await refreshFromApi();
          })();
        case 'UPDATE_OPPORTUNITY':
          return (async () => {
            await exitApi.updateOpportunity(action.opportunityId, action.changes);
            await refreshFromApi();
          })();
        case 'RESUME_OPPORTUNITY':
          return (async () => {
            await exitApi.resumeOpportunity(action.opportunityId);
            await refreshFromApi();
          })();
        case 'UPDATE_PROJECT_STAGE':
          return (async () => {
            await exitApi.updateProjectStage(action.projectId, action.stage);
            await refreshFromApi();
          })();
        case 'UPDATE_PROJECT':
          return (async () => {
            await exitApi.updateProject(action.projectId, {
              owner: action.changes.owner,
              nextAction: action.changes.nextAction,
              exitMethod: action.changes.plan?.method,
              exitType: action.changes.plan?.exitType,
              targetExitAmount: action.changes.plan?.targetExitAmount,
            });
            await refreshFromApi();
          })();
        case 'UPDATE_PLAN':
          return (async () => {
            await exitApi.updateProject(action.projectId, {
              exitMethod: action.changes.method,
              exitType: action.changes.exitType,
              targetExitAmount: action.changes.targetExitAmount,
            });
            await refreshFromApi();
          })();
        case 'RESUME_PROJECT':
          return (async () => {
            await exitApi.resumeProject(action.projectId);
            await refreshFromApi();
          })();
        case 'UPDATE_TRANSACTION_STAGE':
          return (async () => {
            await exitApi.updateTransactionStage(action.transactionId, action.stage);
            await refreshFromApi();
          })();
        case 'RESUME_TRANSACTION':
          return (async () => {
            await exitApi.resumeTransaction(action.transactionId);
            await refreshFromApi();
          })();
        case 'COMPLETE_CLOSING':
          return (async () => {
            await exitApi.completeTransaction(action.transactionId);
            await refreshFromApi();
          })();
        case 'UPDATE_CONDITION':
          return (async () => {
            await exitApi.updateCondition(action.conditionId, action.changes);
            await refreshFromApi();
          })();
        case 'UPDATE_BUYER':
          return (async () => {
            await exitApi.updateBuyer(action.buyerId, action.changes);
            await refreshFromApi();
          })();
        case 'RECORD_PAYMENT':
          return (async () => {
            const plan = stateRef.current.paymentPlans[action.paymentPlanId];
            if (!plan) return;
            await exitApi.createPaymentRecord({
              paymentPlanId: action.paymentPlanId,
              transactionId: plan.transactionId,
              amount: action.amount,
              receivedAt: action.paidDate,
              proofAttachmentUuid: action.proofAttachmentUuid,
              remark: action.remark,
            });
            await refreshFromApi();
          })();
        case 'REVERSE_PAYMENT':
          return (async () => {
            await exitApi.reversePaymentRecord(action.paymentRecordId, action.reason);
            await refreshFromApi();
          })();
        default:
          // Non-persisted prototype actions are intentionally unavailable in
          // the connected application; do not present optimistic local state.
          return;
      }
    },
    [refreshFromApi],
  );

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        <ApiContext.Provider value={{ apiReady, refreshFromApi }}>
          <ExitDataGate>{children}</ExitDataGate>
        </ApiContext.Provider>
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useExitState = () => {
  const value = React.useContext(StateContext);
  if (!value)
    throw new Error('useExitState must be used inside ExitManagementProvider');
  return value;
};

export const useExitDispatch = () => {
  const value = React.useContext(DispatchContext);
  if (!value)
    throw new Error(
      'useExitDispatch must be used inside ExitManagementProvider',
    );
  return value;
};

export const useExitApi = () => {
  const value = React.useContext(ApiContext);
  if (!value)
    throw new Error('useExitApi must be used inside ExitManagementProvider');
  return value;
};

export const useOpportunity = (id?: string) => {
  const state = useExitState();
  return id ? state.opportunities[id] : undefined;
};

export const useExitProject = (id?: string) => {
  const state = useExitState();
  return id ? state.projects[id] : undefined;
};

export const useExitTransaction = (id?: string) => {
  const state = useExitState();
  return id ? state.transactions[id] : undefined;
};

const ExitDataGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { apiReady } = useExitApi();
  const { currentUser, loading: sessionLoading } = useSession();

  if (
    !sessionLoading &&
    hasOAuth2Session() &&
    currentUser &&
    !apiReady
  ) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
        }}
      >
        <Spin />
      </div>
    );
  }

  return <>{children}</>;
};
